import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { login, verify2FA, pending2FA, cancelPending2FA, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // Если requires2FA — контекст выставит pending2FA и UI переключится на шаг кода.
      // Иначе — isAuthenticated станет true и редирект сработает выше.
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка входа';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(code.trim());
      navigate('/');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Неверный код';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(13, 20, 40, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: isMobile ? '1.5rem' : '2rem',
    border: '1px solid rgba(212, 175, 55, 0.12)',
    boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
    position: 'relative' as const,
  };

  const goldAccentLine = <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />;

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '1.5rem' : '1.875rem',
    fontWeight: 'bold',
    fontFamily: 'var(--font-display)',
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '0.5rem',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: isMobile ? '0.6rem 0.8rem' : '0.75rem 1rem',
    background: 'rgba(13, 20, 40, 0.6)',
    border: '1px solid rgba(212, 175, 55, 0.15)',
    borderRadius: '0.5rem',
    color: '#f0f0f0',
    fontSize: isMobile ? '0.875rem' : '1rem',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#b0b0b0',
    marginBottom: '0.5rem',
  };

  const btnGoldStyle: React.CSSProperties = {
    width: '100%',
    padding: isMobile ? '0.65rem' : '0.75rem',
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4a6 100%)',
    color: '#0a0814',
    borderRadius: '0.5rem',
    fontWeight: '600',
    fontSize: isMobile ? '0.875rem' : '1rem',
    border: 'none',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.5 : 1,
  };

  // ── Шаг 2: ввод кода 2FA ──
  if (pending2FA) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)', padding: isMobile ? '16px' : '0' }}>
        <div style={{ width: '100%', maxWidth: '28rem', margin: '0 1rem' }}>
          <div style={cardStyle}>
            {goldAccentLine}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ fontSize: isMobile ? '2.5rem' : '3rem', marginBottom: '0.5rem' }}>🔐</div>
              <h1 style={{ ...titleStyle, textAlign: 'center', fontSize: isMobile ? '1.25rem' : '1.5rem' }}>Двухфакторная аутентификация</h1>
              <p style={{ color: '#b0b0b0', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>Введите 6-значный код из приложения-аутентификатора</p>
            </div>

            <form onSubmit={handle2FASubmit} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1rem' : '1.5rem' }}>
              {error && (
                <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '0.5rem', padding: '0.75rem', color: '#fecaca', fontSize: '0.875rem' }}>
                  {error}
                </div>
              )}

              <div>
                <label style={labelStyle}>Код 2FA</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  style={{ ...inputStyle, fontSize: isMobile ? '1.25rem' : '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' }}
                  placeholder="000000"
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                style={{ ...btnGoldStyle, cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer', opacity: loading || code.length !== 6 ? 0.5 : 1 }}
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>

              <button
                type="button"
                onClick={cancelPending2FA}
                style={{ width: '100%', padding: '0.5rem', background: 'transparent', color: '#b0b0b0', border: 'none', cursor: 'pointer', fontSize: isMobile ? '0.8rem' : '0.875rem' }}
              >
                ← Назад к входу
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Шаг 1: email + пароль ──
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 100%)', padding: isMobile ? '16px' : '0' }}>
      <div style={{ width: '100%', maxWidth: '28rem', margin: '0 1rem' }}>
        <div style={cardStyle}>
          {goldAccentLine}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={titleStyle}>Astranum Admin</h1>
            <p style={{ color: '#b0b0b0' }}>Панель администратора</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1rem' : '1.5rem' }}>
            {error && (
              <div style={{ background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.5)', borderRadius: '0.5rem', padding: '0.75rem', color: '#fecaca', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
                placeholder="info@astranum.pro"
              />
            </div>

            <div>
              <label style={labelStyle}>Пароль</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={inputStyle}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={btnGoldStyle}
            >
              {loading ? 'Вход...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
